import { useEffect } from 'react';

/** Reference-counted body scroll lock. Multiple modals open at once → the
 *  body stays locked until ALL of them release; nested locks don't fight
 *  each other.
 *
 *  Also compensates for the disappearing scrollbar: when overflow flips
 *  to `hidden`, the page would otherwise jump rightward by the scrollbar
 *  width as the gutter collapses. We add `paddingRight` equal to the
 *  scrollbar width while locked so the visible content stays put.
 *
 *  Saved state (overflow + paddingRight) is captured ONCE on the first
 *  lock and restored on the last unlock, so the restored values reflect
 *  the page's actual prior style — not whatever some intermediate code
 *  set in the meantime. */
let lockCount = 0;
let savedOverflow = '';
let savedPaddingRight = '';

function getScrollbarWidth(): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  // The viewport width minus the layout (clientWidth) of the document
  // element gives the gutter the scrollbar currently occupies. 0 when
  // no scrollbar (short pages, overlay scrollbars on macOS).
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
}

function lock(): void {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) {
    const sbw = getScrollbarWidth();
    savedOverflow = document.body.style.overflow;
    savedPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (sbw > 0) {
      // Add to whatever padding the body already has so we don't clobber
      // app-level padding. Read computed (not inline) to pick up CSS too.
      const existing =
        Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${String(existing + sbw)}px`;
    }
  }
  lockCount += 1;
}

function unlock(): void {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount === 0) {
    document.body.style.overflow = savedOverflow;
    document.body.style.paddingRight = savedPaddingRight;
  }
}

/** Lock body scroll while `active` is true. The lock is reference-counted
 *  internally so multiple components can call this without conflict. */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    lock();
    return () => {
      unlock();
    };
  }, [active]);
}

/** Test-only escape hatch: forcibly reset the lock counter and restore
 *  inline styles. Use between test cases that may have leaked locks. */
export function __resetBodyScrollLockForTests(): void {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = savedOverflow;
    document.body.style.paddingRight = savedPaddingRight;
  }
  lockCount = 0;
  savedOverflow = '';
  savedPaddingRight = '';
}
